/**
 * 테마 (라이트 / 다크).
 *
 * html.dark 클래스 토글로 동작. 다크가 default (기존 admin 의 톤 유지) — 새로 들어온
 * 사용자에게 깜빡임 줄이려고 localStorage 없을 때도 다크부터 보여준다.
 */

import { useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

const LS_KEY = 'sayknowwork-admin-theme';
const DEFAULT_THEME: Theme = 'dark';

function readInitial(): Theme {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const saved = window.localStorage.getItem(LS_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch { /* private mode */ }
  return DEFAULT_THEME;
}

let currentTheme: Theme = readInitial();

function applyToDOM(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

// 앱 부팅 직후 1회 동기화 — module import 시점
applyToDOM(currentTheme);

const listeners = new Set<() => void>();
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
};
const getSnapshot = () => currentTheme;

export function setTheme(next: Theme) {
  currentTheme = next;
  applyToDOM(next);
  try { window.localStorage.setItem(LS_KEY, next); } catch { /* ignore */ }
  listeners.forEach((l) => l());
}

export function toggleTheme() {
  setTheme(currentTheme === 'dark' ? 'light' : 'dark');
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { theme, setTheme, toggleTheme };
}
